import { ComponentType, FC, useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import Http from '@/support/http';
import { api as apiRoute, web as webRoutes } from '@/support/route';
import Loading from '@/components/Loading';
import * as Auth from '@/support/auth';
import { notAborted, useAbortController } from '@/support/Hooks/fetch/useAbortController';

interface WithStudyV2Props {
  includes?: string;
  restrictedAccessFns?: Array<(study: unknown) => boolean>;
}

interface StudyParams {
  id?: string;
}

interface GlossaryItem {
  group: string;
  [key: string]: unknown;
}

/**
 * HOC that fetches study data before rendering component
 * Uses useNavigate instead of useHistory (v6 migration)
 */
export const withStudyV2 =
  <P extends object>(Component: ComponentType<P>, { includes = '', restrictedAccessFns = [] }: WithStudyV2Props) =>
  (props: P) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const { newAbortController } = useAbortController();
    const dispatch = useDispatch();
    const params = useParams<keyof StudyParams>();
    const navigate = useNavigate();

    const fetchStudy = async () => {
      setLoading(true);
      try {
        const apiParams = {
          id: params.id,
        };
        let queryParams = {};
        if (includes !== '') {
          queryParams = { include: includes };
        }
        const {
          data: { data },
        } = await Http.get(apiRoute('studies.show.p', apiParams, queryParams), { signal: newAbortController().signal });
        dispatch({
          type: 'SET_CURRENT_STUDY',
          study: data,
        });
        if (restrictedAccessFns?.some((restrictedAccessFn) => restrictedAccessFn(data))) {
          navigate(webRoutes('studies.show', { id: params.id }), { replace: true });
        }
        setLoading(false);
      } catch (err) {
        if (notAborted(err)) {
          setError(true);
          setLoading(false);
        }
      }
    };

    useEffect(() => {
      fetchStudy();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (loading) {
      return <Loading />;
    }
    if (error) {
      return <div>error</div>;
    }
    return <Component {...props} />;
  };

/**
 * HOC that fetches study data before rendering component (legacy version)
 * Uses useNavigate instead of useHistory (v6 migration)
 */
export const withStudy =
  <P extends object>(Component: ComponentType<P>, restrictedAccessFns?: Array<(study: unknown) => boolean>) =>
  (props: P) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const { newAbortController } = useAbortController();
    const dispatch = useDispatch();
    const params = useParams<keyof StudyParams>();
    const navigate = useNavigate();

    const fetchStudy = async () => {
      setLoading(true);
      try {
        const {
          data: { data },
        } = await Http.get(apiRoute('studies.show', { id: params.id, include: 'project' }), {
          signal: newAbortController().signal,
        });
        dispatch({
          type: 'SET_CURRENT_STUDY',
          study: data,
        });
        const hasRestrictedAccess =
          Array.isArray(restrictedAccessFns) &&
          restrictedAccessFns.length > 0 &&
          restrictedAccessFns.some((restrictedAccessFn) => restrictedAccessFn(data));
        if (hasRestrictedAccess) {
          navigate(webRoutes('studies.show', { id: params.id }), { replace: true });
        }
        setLoading(false);
      } catch (err) {
        if (notAborted(err)) {
          setError(true);
          setLoading(false);
        }
      }
    };

    useEffect(() => {
      fetchStudy();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (loading) {
      return <Loading />;
    }
    if (error) {
      return <div>error</div>;
    }
    return <Component {...props} />;
  };

/**
 * HOC that fetches glossary data before rendering component
 */
export const withGlossary =
  <P extends object>(Component: ComponentType<P & { glossary: GlossaryItem[] }>, group = 'observations') =>
  (props: P) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [glossary, setGlossary] = useState<GlossaryItem[]>([]);
    const { newAbortController } = useAbortController();

    const fetchGlossary = async () => {
      setLoading(true);
      try {
        const {
          data: { data },
        } = await Http.get(apiRoute('glossary.show'), { signal: newAbortController().signal });
        setGlossary(data);
        setLoading(false);
      } catch (err) {
        if (notAborted(err)) {
          setError(true);
          setLoading(false);
        }
      }
    };

    useEffect(() => {
      fetchGlossary();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (loading) {
      return <Loading txt="Loading Glossary..." />;
    }
    if (error) {
      return <div>error</div>;
    }
    return <Component {...props} glossary={glossary.filter((item) => item.group === group)} />;
  };

/**
 * HOC that wraps component with spacing
 */
export const withSpacing =
  <P extends object>(Component: ComponentType<P>): FC<P> =>
  (props: P) => (
    <div className="ph4 pb5 w-100 h-100">
      <Component {...props} />
    </div>
  );

/**
 * HOC that restricts access to team owners only
 * Converted from v5 Route+Redirect pattern to v6 Navigate component
 */
export const onlyTeamOwners =
  <P extends object>(Component: ComponentType<P>): FC<P> =>
  (props: P) => {
    const location = useLocation();

    if (!Auth.isCurrentTeamOwner()) {
      return <Navigate to="/403" state={{ from: location }} replace />;
    }

    return <Component {...props} />;
  };

/**
 * HOC that restricts access to support users only
 * Converted from v5 Route+Redirect pattern to v6 Navigate component
 */
export const onlySupportUsers =
  <P extends object>(Component: ComponentType<P>): FC<P> =>
  (props: P) => {
    const location = useLocation();

    if (!Auth.isCurrentTeamAdminConsoleUser()) {
      return <Navigate to="/403" state={{ from: location }} replace />;
    }

    return <Component {...props} />;
  };

/**
 * HOC that resets Redux settings on mount
 */
export const resetReduxSettings =
  <P extends object>(Component: ComponentType<P>): FC<P> =>
  (props: P) => {
    const dispatch = useDispatch();

    useEffect(() => {
      dispatch({ type: 'RESET_SETTINGS' });
    }, [dispatch]);

    return <Component {...props} />;
  };
